import { Input } from "@chakra-ui/input";
import {
  Divider,
  Text,
  Flex,
  PopoverArrow,
  PopoverContent,
  Button,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { isEmpty } from "lodash";
import { forwardRef, useContext, useEffect, useMemo, useState } from "react";
import { MdsChevronRightRound } from "react-icons-with-materialsymbols/mds";
import { usePreviousDistinct } from "react-use";

import { Icon } from "@/design/components/icon";
import {
  FilterContext,
  FilterCondition,
  CONDITIONS_MAP,
  MULTIPLE_INPUT,
  FILTER_TYPE,
  ViewFiltersRequest,
  getAllFilterOptions,
  isInvalidFilter,
  seperateFilters,
  COMMA_SEPARATOR,
  NO_CONDITION,
  usePaginatedFilterStats,
} from "@/features/data-transformation";
import { AllFiltersError } from "@/features/data-transformation/components/filter-dropdown/all-filters-error.tsx";
import { StepFormContext } from "@/features/data-transformation/components/step-form/step-form-context.ts";
import { normalizeValue } from "@/features/data-transformation/utils/helpers";

import { searchedValues } from "../../utils/search-utils.ts";

import { ColumnDropdown } from "./column-dropdown.tsx";
import { FilterByCondition } from "./filter-by-condition.tsx";
import { FilterByValueRow } from "./filter-by-value-row.tsx";
import { FilterDetailsContext } from "./filter-details-context.ts";
import { hasFilterChanged } from "../../utils/filter-utils.ts";

type FilterDropdownProps = {
  col?: string;
  closeFilter: () => void;
  setAllFilterOptions: (value: any) => void;
  isOpen: boolean;
};

export const FilterDropdown = forwardRef<HTMLDivElement, FilterDropdownProps>(
  function ({ col, setAllFilterOptions, closeFilter, isOpen }, ref) {
    const { step } = useContext(StepFormContext) ?? {};

    const {
      appliedFilters: filters,
      filter,
      editDisabled,
      placement,
      changeTempFilter,
      updateSavedFilters,
      changeCurrentOpenFilter,
    } = useContext(FilterContext)!;

    const [openByCondition, setOpenByCondition] = useState(false);
    const [openByValue, setOpenByValue] = useState(true);
    const [column, setColumn] = useState<string | null>(col ?? null);
    const [error, setError] = useState(false);

    const [input1, setInput1] = useState<string>("");
    const [input2, setInput2] = useState<string>("");

    const [selectedCondition, setSelectedCondition] =
      useState<FilterCondition | null>(null);

    const [selectAll, setSelectAll] = useState(true);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [localMatch, setLocalMatch] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const previousSearchTerm = usePreviousDistinct(searchTerm);
    const [selectAllSearchTerm, setSelectAllSearchTerm] = useState<string>("");

    const {
      filterStatsData,
      totalValuesCount,
      filterItems,
      setFilterItems,
      isLoading,
      isPaginating,
      hasMore,
      handleSearchChange,
      fetchNextPage,
      resetData: resetPaginationData,
    } = usePaginatedFilterStats({
      column,
      isOpen,
      filters: placement === "bottom" ? seperateFilters(filters) : [],
      transformationStepId: step?.transformationStepId,
    });

    useEffect(() => {
      if (!filterStatsData) return;

      const allOptions = getAllFilterOptions(filterStatsData);
      const localSearchedValues = searchedValues(allOptions, filter.search);

      setAllFilterOptions((prevAllOptions: string[]) => {
        const isFirstLoad = isEmpty(prevAllOptions);

        const newOptions = allOptions.filter(
          (option) => !prevAllOptions.includes(option)
        );

        const isSearchFilter = filter.search && filter.selectAll;
        const isNewSearch = previousSearchTerm !== searchTerm;

        if (isFirstLoad) {
          if (isEmpty(selectedValues) && !isSearchFilter) {
            setSelectedValues(allOptions);
            setLocalMatch(false);
          } else if (isSearchFilter) {
            setSelectedValues(localSearchedValues);
            setLocalMatch(true);
          }
        } else {
          const previousSelectedCheck = prevAllOptions.every((option: string) =>
            selectedValues.includes(option)
          );
          const allPreviousOptionsSelected =
            !isEmpty(prevAllOptions) && previousSelectedCheck;

          if (allPreviousOptionsSelected && !localMatch) {
            setSelectedValues(allOptions);
            setLocalMatch(false);
          } else if (isSearchFilter) {
            setSelectedValues(localSearchedValues);
            setLocalMatch(true);
          }
        }

        if (isNewSearch) {
          return allOptions;
        }

        return [...prevAllOptions, ...newOptions];
      });
    }, [filterStatsData]);

    const isSavedFilter = useMemo(() => {
      const isSaved = filters.find((_item) => _item.column === filter.column);
      return !!isSaved;
    }, [filter, filters]);

    useEffect(() => {
      if (filter.filterType === FILTER_TYPE.CONDITION) {
        setOpenByCondition(true);
        setOpenByValue(false);
      } else {
        setOpenByCondition(false);
        setOpenByValue(true);
      }

      const _selectedCondition = CONDITIONS_MAP.find(
        (item) => item.value === filter.conditionType
      );

      const columnChange = filter.column !== column;

      if (columnChange) {
        setColumn(filter.column);
        resetPaginationData();
      }

      if (filter.values) {
        setSelectedValues(filter.values);
      }

      if (!_selectedCondition) return;
      setInitialValuesForCondition(_selectedCondition);
    }, [filter]);

    const setInitialValuesForCondition = (
      _selectedCondition: FilterCondition | null
    ) => {
      setSelectedCondition(_selectedCondition);

      if (!_selectedCondition) {
        setInput1("");
        setInput2("");
        return;
      }

      const _isMultipleInput = MULTIPLE_INPUT.find(
        (item) => item.key === _selectedCondition.key
      );
      if (_isMultipleInput) {
        const [_input1, _input2]: string[] =
          filter.value.split(COMMA_SEPARATOR);
        setInput1(_input1);
        setInput2(_input2);
      } else {
        setInput1(filter?.value as string);
      }
    };

    const onOpenByCondition = () => {
      setOpenByCondition(!openByCondition);
      if (!openByCondition) {
        setOpenByValue(false);
      }
    };

    const isMultipleInput = useMemo(() => {
      if (selectedCondition) {
        return MULTIPLE_INPUT.find(
          (item) => item.key === selectedCondition.key
        );
      }
      return false;
    }, [selectedCondition]);

    const onOpenByValue = () => {
      setOpenByValue(!openByValue);
      if (!openByValue) {
        setOpenByCondition(false);
      }
    };

    const changeColumn = (_column: string) => {
      setColumn(_column);
      resetPaginationData();
      setSelectedValues([]);
      setSelectAll(true);

      if (isSavedFilter) {
        const oldFilter = filters.find((_filter) => col === _filter.column)!;
        const updatedAppliedFilters = getUpdatedFilters(oldFilter);
        updateSavedFilters(updatedAppliedFilters, true);
      }

      const newFilter = {
        filterType: filter.filterType,
        column: _column,
      };

      // Reset selectAll search state when column changes
      setSelectAllSearchTerm("");

      changeCurrentOpenFilter(_column);
      changeTempFilter(newFilter);
    };

    const isAllSelected = (values: string[]) => {
      const allOptions = getAllFilterOptions(filterStatsData);
      const selectAllCheck = selectAllSearchTerm !== "" && selectAll;
      const valueCheck =
        !isEmpty(values) &&
        values.length === allOptions.length &&
        allOptions.every((option) => values.includes(option));
      /**
       * Return false if selectAll is true and search term is not empty
       * so that we do not eliminate the filter by value
       **/  
      return selectAllCheck  ? false : valueCheck;
    };

    const filterType = () => {
      const allSelected = isAllSelected(selectedValues);

      const hasFilterCondition = !!selectedCondition?.value;
      const isNone = selectedCondition?.value === NO_CONDITION;

      const isBoth = hasFilterCondition && !allSelected && !isNone;

      if (hasFilterCondition && allSelected) {
        return FILTER_TYPE.CONDITION;
      } else if (isBoth) {
        return FILTER_TYPE.BOTH;
      } else {
        return FILTER_TYPE.VALUE;
      }
    };

    const onSearchChange = (term: string) => {
      const trimmedTerm = term.trim();
      setSearchTerm(trimmedTerm);
      handleSearchChange(trimmedTerm);
    };

    const getUpdatedFilter = (): ViewFiltersRequest => {
      const _inputValue = isMultipleInput
        ? `${input1}${COMMA_SEPARATOR}${input2}`
        : input1;
      const commonValues = {
        ...filter,
        column: column!,
      };

      // Determine filter type based on inputs (condition, selected values)
      switch (filterType()) {
        case FILTER_TYPE.CONDITION:
          return {
            column: commonValues.column,
            id: commonValues?.id,
            conditionType: selectedCondition!.value ?? "",
            value: _inputValue,
            filterType: FILTER_TYPE.CONDITION,
          };
        case FILTER_TYPE.VALUE: {
          // If selectAll is true (but no search term), send selectAll flag
          if (selectAll) {
            return {
              column: commonValues.column,
              id: commonValues?.id,
              values: [], // Send empty values when selectAll is true
              filterType: FILTER_TYPE.VALUE,
              selectAll: true,
              search: selectAllSearchTerm || filter.search,
            };
          }
          // Otherwise, return standard VALUE payload with selected values
          return {
            column: commonValues.column,
            id: commonValues?.id,
            values: selectedValues,
            filterType: FILTER_TYPE.VALUE,
            // selectAll is implicitly false here
          };
        }
        case FILTER_TYPE.BOTH: {
          // Handle selectAll=true but no search term for BOTH
          if (selectAll) {
            return {
              ...commonValues,
              conditionType: selectedCondition!.value ?? "",
              value: _inputValue,
              filterType: FILTER_TYPE.BOTH,
              values: [], // Send empty values
              selectAll: true,
              search: selectAllSearchTerm || filter.search,
            };
          }
          // Standard BOTH payload with condition and specific values
          return {
            ...commonValues,
            conditionType: selectedCondition!.value ?? "",
            value: _inputValue,
            values: selectedValues,
            filterType: FILTER_TYPE.BOTH,
          };
        }
      }
    };

    const getUpdatedFilters = (updatedFilter: ViewFiltersRequest) => {
      let updatedAppliedFilters = filters.filter(
        (_filter) => _filter.id !== updatedFilter.id
      );
      updatedAppliedFilters = updatedAppliedFilters.map((_filter, index) => {
        return { ..._filter, id: index.toString() };
      });

      return updatedAppliedFilters;
    };
    const removeInvalidFilter = (updatedFilter: ViewFiltersRequest) => {
      if (isSavedFilter) {
        const updatedAppliedFilters = getUpdatedFilters(updatedFilter);
        updateSavedFilters(updatedAppliedFilters);
      }
      changeTempFilter(null);
    };

    const applyFilter = () => {
      const updatedFilter = getUpdatedFilter();

      const changed = hasFilterChanged(filter, updatedFilter);
      if (!changed) {
        closeFilter();
        return;
      }

      if (isInvalidFilter(updatedFilter)) {
        removeInvalidFilter(updatedFilter);
        closeFilter();
        return;
      }

      setError(false);

      let updatedAppliedFilters = [];

      if (isSavedFilter) {
        updatedAppliedFilters = filters.map((_filter) => {
          if (_filter.column === updatedFilter.column) {
            return updatedFilter;
          }
          return _filter;
        });
      } else {
        updatedFilter.id = (filters.length + 1).toString();
        updatedAppliedFilters = [...filters, updatedFilter];
      }

      if (updatedFilter.filterType === FILTER_TYPE.CONDITION) {
        fetchNextPage();
      }
      if (updatedFilter.selectAll && !isEmpty(updatedFilter.search)) {
        setAllFilterOptions([]);
      }

      updateSavedFilters(updatedAppliedFilters);
      changeTempFilter(null);
      setSelectAllSearchTerm("");
      setSearchTerm("");
      setLocalMatch(false);

      closeFilter();
    };

    const onCancel = () => {
      if (!isSavedFilter) {
        changeTempFilter(null);
      } else {
        const oldFilter = filters.find((_filter) => _filter.column === column);
        if (oldFilter) {
          if (oldFilter.values) {
            setSelectedValues([...oldFilter.values]);
          }
          if (oldFilter.conditionType) {
            const _selectedCondition =
              CONDITIONS_MAP.find(
                (item) => item.value === oldFilter.conditionType
              ) ?? null;
            setInitialValuesForCondition(_selectedCondition);
          }
        }
        changeCurrentOpenFilter(null);
      }
      // Reset selectAll search state on cancel
      setSelectAllSearchTerm("");
      closeFilter();
    };

    const onClear = () => {
      setSelectedValues([]);
      setSelectAllSearchTerm("");
      setSelectAll(false);
    };

    const onSelectAll = () => {
      const allAvailableOptions = getAllFilterOptions(filterStatsData); // All available options
      const allDisplayedOptions = filterItems ?? []; // Options currently displayed

      // Always set selectAll to true when the button is clicked
      setSelectAll(true);

      if (searchTerm) {
        setSelectedValues(allDisplayedOptions);
        setSelectAllSearchTerm(searchTerm);
      } else {
        setSelectedValues(allAvailableOptions);
        setSelectAllSearchTerm("");
      }
    };

    const onSelectValue = (value: string) => {
      const updatedValue = normalizeValue(value);
      let newSelectedValues: string[];

      if (selectedValues.some((i) => normalizeValue(i) === updatedValue)) {
        newSelectedValues = selectedValues.filter(
          (i) => normalizeValue(i) !== updatedValue
        );
      } else {
        newSelectedValues = [...selectedValues, value];
      }
      setSelectedValues(newSelectedValues);
      setSelectAllSearchTerm("");
      setSelectAll(false);
    };

    const onConditionChange = (_condition: FilterCondition) => {
      setSelectedCondition(_condition);
      setInput1("");
      setInput2("");
    };
    const showInput = useMemo(
      () => !(selectedCondition?.hasNoInput ?? false),
      [selectedCondition]
    );

    return (
      <PopoverContent
        className="!outline-0"
        sx={{
          ":focus-visible": {
            outline: "none",
          },
          "& .chakra-popover__popper": {
            zIndex: "99 !important",
          },
        }}
      >
        <PopoverArrow />
        <FilterDetailsContext.Provider
          value={{
            openByValue,
            onOpenByValue,
            selectedValues,
            initialData: {
              search: filter.search,
              selectAll: filter.selectAll,
            },
            allValues: filterItems,
            onSelectValue: onSelectValue,
            setSelectedValues,
            selectAll,
            onSelectAll,
            onClear,
            filterItems,
            setFilterItems,
            paginationDetails: {
              totalValuesCount,
              isLoading,
              isPaginating,
              hasMore,
              fetchNextPage,
              handleSearchChange: onSearchChange,
            },
          }}
        >
          <Flex className="p-4 flex-col gap-y-5" ref={ref}>
            <ColumnDropdown
              selectedColumn={column}
              setSelectedColumn={changeColumn}
              isSavedFilter={isSavedFilter}
            />
            {!column && (
              <Text className="text-[13px] leading-[120%] tracking-[-0.26px]">
                Select column to apply filters
              </Text>
            )}
            {column && (
              <>
                <Divider color="gray.200" />
                <Flex className="items-center gap-x-2 text-[13px] leading-none font-semibold uppercase">
                  <Flex
                    as={motion.div}
                    color="gray.500"
                    cursor="pointer"
                    animate={{
                      rotate: !openByCondition ? 0 : 90,
                      transition: { duration: 0.2, type: "spring" },
                    }}
                    aria-label="collapse"
                    onClick={onOpenByCondition}
                  >
                    <Icon as={MdsChevronRightRound} size="md" />
                  </Flex>
                  Filter By Condition
                </Flex>
                {openByCondition && (
                  <>
                    <FilterByCondition
                      col={col}
                      selectedCondition={selectedCondition}
                      onChange={onConditionChange}
                    />

                    {showInput && (
                      <Flex className="flex-col items-start text-[13px] gap-y-2">
                        <Input
                          placeholder="Value or Formula"
                          size="sm"
                          className="!rounded-[3px] !border-gray-500 placeholder-gray-500"
                          value={input1}
                          onChange={(e) => setInput1(e.target.value)}
                        />
                        {isMultipleInput && (
                          <>
                            <Text className="font-medium ml-2">and</Text>
                            <Input
                              placeholder="Value or Formula"
                              size="sm"
                              className="!rounded-[3px] !border-gray-500 placeholder-gray-500"
                              value={input2}
                              onChange={(e) => setInput2(e.target.value)}
                            />
                          </>
                        )}
                      </Flex>
                    )}
                  </>
                )}
                <FilterByValueRow />
                <AllFiltersError error={error} />
                {!editDisabled && (
                  <Flex className="justify-end items-center gap-x-4">
                    <Button
                      colorScheme="secondary"
                      onClick={onCancel}
                      size="sm"
                      variant="outline"
                    >
                      Cancel
                    </Button>
                    <Button onClick={applyFilter} size="sm">
                      Apply Filter
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </Flex>
        </FilterDetailsContext.Provider>
      </PopoverContent>
    );
  }
);
